import AUTH_POLICY from '#client/constants/authPolicy';
/** @typedef {import('react').ComponentType} ComponentType */
/** @typedef {import('./flowPageConfig').Page} Page */

/**
 * @typedef {object} MergeConfig
 * @property {'insert'|'replace'} type - The type of merge operation
 * @property {string} [inKey] - The key in the flow config where to insert values (required for 'insert' type)
 * @property {string} [afterValue] - The value after which to insert new values (required for 'insert' type)
 */

/**
 * @typedef {object} FeatureFlagConfig
 * @property {boolean} isExperiment - Whether this flag should fire an experiment exposed event
 * @property {MergeConfig} merge - Configuration for how to merge the variation with the flow config
 * @property {object} variations - The variations for this feature flag
 */

/**
 * @typedef {object} PriceListUuids
 * @property {?string} development - UUID for the development environment
 * @property {?string} production - UUID for the production environment
 */

/**
 * @typedef {{[key: string]: Page}} PageConfigs
 * @property {string} pageComponent - The page component to configure
 */

/**
 * @typedef {object} ScreensToSkipByService
 * @property {?string} pathToValidate - The path of the page to validate
 * @property {?string} skipInService - The service name that makes the page skip
 */

/**
 * @typedef {object} FlowConfigOptions
 * @property {boolean} [autoLoadBusiness] - Whether to autoload business information
 * @property {?string} [authRedirect] - Redirect URL for authenticated users
 * @property {?object<string, string>} [authRedirectOverrides] - Optional overrides for specific routes
 * @property {boolean} [authPolicy] - The authentication policy to be applied for the flow
 * @property {boolean} [autoResume] - Whether to automatically resume progress on flow entry
 * @property {?object<string, string>} [clickStreamProps] - Properties to be passed to the clickstream scene
 * @property {?string} [currentExperiment] - Current experiment the flow is under
 * @property {boolean} [disableCartCreation] - Whether cart creation is disabled for this flow
 * @property {boolean} [disableLeadManager] - Whether the lead manager is disabled for this flow
 * @property {string} [entityType] - The type of entity (e.g., LLC)
 * @property {?FeatureFlagConfig} [featureFlags] - Feature flags configurations for the flow
 * @property {ComponentType} [errorBoundaryFallback] - The fallback element for the error boundary
 * @property {boolean} [hasFormation] - Whether the flow involves entity formation
 * @property {string} id - Unique identifier for the flow (required)
 * @property {boolean} [isPrimary] - A flag to distinguish between primary and secondary flows. A primary flow will fire Flow Initialized marketing events.
 * @property {boolean} [isSCorp] - A flag to distinguish when a flow is for S-Corp entities
 * @property {PageConfigs} [pageConfigs] - Config overrides for individual pages
 * @property {string[]} pageKeys - Array of page keys in kebab-case format to resolve into configurations
 * @property {ComponentType} [persistenceGate] - The component responsible for adding/saving data to the flow state
 * @property {boolean} [postOrderAutoLogin] - Whether auto-login is enabled post-order
 * @property {?string} [postOrderRedirect] - Redirect URL post-order
 * @property {PriceListUuids} [priceListUuids] - UUIDs for price lists in different environments
 * @property {string[]} [requiredEntitlements] - The entitlements required for the flow
 * @property {?Array<ScreensToSkipByService>} [screensToSkipByService] - The screens to skip by service
 * @property {number} [version] - The version of the flow configuration
 */

/**
 * Class representing a flow configuration.
 */
class FlowConfig {
  /**
   * Creates a new instance of FlowConfig.
   *
   * @param {FlowConfigOptions} data - Configuration options for the flow
   * @returns {FlowConfig} A new instance of FlowConfig
   */
  static createEntry(data) {
    return new FlowConfig(data);
  }

  /**
   * Returns the default configuration values for a flow.
   *
   * @returns {FlowConfigOptions} The default configuration values
   */
  static defaultOptions() {
    return {
      pageComponentProps: {},
      pageConfigs: {},
      authRedirect: null,
      authRedirectOverrides: {},
      authPolicy: AUTH_POLICY.NOT_ENFORCED,
      autoLoadBusiness: false,
      autoResume: true,
      clickStreamProps: {},
      currentExperiment: null,
      disableCartCreation: false,
      disableLeadManager: false,
      entityType: '',
      hasFormation: false, // TODO: This can be computed from the pageKeys
      isPrimary: false, // TODO: Settle on a property name to distinguish between first and second checkout flow.
      isSCorp: false,
      postOrderAutoLogin: false,
      postOrderRedirect: null,
      priceListUuids: {
        development: null,
        production: null
      },
      errorBoundaryFallback: null,
      persistenceGate: null,
      requiredEntitlements: [],
      version: 1
    };
  }

  /**
   * Creates a new instance of FlowConfig.
   *
   * @param {FlowConfigOptions} options - Configuration options for the flow
   */
  constructor(options = {}) {
    const defaults = FlowConfig.defaultOptions();
    const config = { ...defaults, ...options };

    if (!config.id) {
      throw new Error("The 'id' property is required for a FlowConfig instance.");
    }

    this.authPolicy = config.authPolicy;
    this.authRedirect = config.authRedirect;
    this.authRedirectOverrides = config.authRedirectOverrides;
    this.autoLoadBusiness = config.autoLoadBusiness;
    this.autoResume = config.autoResume;
    this.clickStreamProps = config.clickStreamProps;
    this.currentExperiment = config.currentExperiment;
    this.disableCartCreation = config.disableCartCreation;
    this.disableLeadManager = config.disableLeadManager;
    this.entityType = config.entityType;
    this.errorBoundaryFallback = config.errorBoundaryFallback;
    this.featureFlags = config.featureFlags;
    this.hasFormation = config.hasFormation;
    this.id = config.id;
    this.isPrimary = config.isPrimary;
    this.isSCorp = config.isSCorp;
    this.pageConfigs = config.pageConfigs;
    this.pageKeys = config.pageKeys;
    this.persistenceGate = config.persistenceGate;
    this.postOrderAutoLogin = config.postOrderAutoLogin;
    this.postOrderRedirect = config.postOrderRedirect;
    this.priceListUuids = config.priceListUuids;
    this.requiredEntitlements = config.requiredEntitlements;
    this.screensToSkipByService = config.screensToSkipByService;
    this.version = config.version;
  }
}

export default FlowConfig;
